import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import {skipped} from "#template/template-node/constants"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BooleanLike} from "#template/nodes/types"
import {booleanLike} from "#template/nodes/boolean/fundamental"
import {TemplateNodes} from "#template/nodes/template-nodes"
import type {NodeOwnerLikeNode} from "#template/role-markers"
import {nodeOwnerMarker} from "#template/role-markers"
import {visitNone, visitAll, visit} from "#template/template-node/visit-helpers"

export type GroupParameters = {
    name: string
    nodes: TemplateNodes
    active: BooleanLike
}

const groupDefinition: TemplateNodeDefinition<GroupParameters> = {
    nodeClass: "Group",
    validation: {
        parameters: z.object({
            name: z.string(),
            nodes: graphNode(TemplateNodes),
            active: booleanLike,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class Group extends TemplateNode<GroupParameters> implements NodeOwnerLikeNode {
    get [nodeOwnerMarker]() {
        return true as const
    }

    public override get definition() {
        return groupDefinition
    }

    public override onFilterActive(context: OnFilterActiveContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<GroupParameters> {
        if (context.evaluator.activeNodeSet === undefined) return skipped

        const {nodes, ...rest} = this.parameters
        const {active} = rest

        const evaluatedActive = context.evaluator.evaluate(active)
        if (typeof evaluatedActive !== "boolean" && evaluatedActive !== null) throw new Error("Active flag must be an instantly evaluable boolean")

        return evaluatedActive
            ? {
                  nodes: visit(nodes, evaluator),
                  ...visitNone(rest),
              }
            : skipped
    }

    public override onCompile(_context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<GroupParameters> {
        const {active, ...rest} = this.parameters
        return {active: skipped, ...visitAll(rest, evaluator)}
    }
}
